import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useGetFeedTeamsQuery } from '@/api/feed';
import { useMemo } from 'react';
import type { FixtureSelection } from '../QuestionCoachModal';

interface TeamSelectionSubstepProps {
  leagueId?: string;
  fixtureTeamIds?: string[]; // The 2 teams from the selected fixture
  selectedTeamId?: string; // Currently selected team ID
  onSelect?: (teamId: string, teamName?: string) => void;
  /** Optional copy override (e.g. Spread uses "Select the favourite team"). */
  title?: string;
  description?: string;
  /**
   * Batch creation mode (DKPLAT-501 Phase 1): renders one group per fixture instead
   * of a single fixture's 2 teams. Use with `mode`:
   * - 'radio': exactly one team per fixture (Spread's favourite team)
   * - 'locked': both teams shown as already-selected, read-only (Moneyline)
   */
  fixtures?: FixtureSelection[];
  mode?: 'radio' | 'locked';
  onSelectForFixture?: (fixtureId: string, teamId: string, teamName?: string) => void;
}

/**
 * Team Selection Substep — shows the 2 teams from the selected fixture and allows
 * selecting exactly 1. Used by player_over_under (pick the player's team) and by the
 * Spread market (pick the favourite team). Also supports a multi-fixture batch mode
 * (DKPLAT-501 Phase 1) for Spread (radio) and Moneyline (locked, read-only).
 */
export function TeamSelectionSubstep({
  leagueId,
  fixtureTeamIds,
  selectedTeamId,
  onSelect,
  title = 'Select Team',
  description = 'Select the team whose player you want to track for this over/under question.',
  fixtures,
  mode,
  onSelectForFixture,
}: TeamSelectionSubstepProps) {
  const { data: teams, isLoading, error } = useGetFeedTeamsQuery(leagueId);

  // Filter to only show the 2 teams from the fixture
  const fixtureTeams = useMemo(() => {
    if (!fixtureTeamIds || fixtureTeamIds.length !== 2 || !teams) {
      return [];
    }
    return teams.filter(team => fixtureTeamIds.includes(team.externalId));
  }, [teams, fixtureTeamIds]);

  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => map.set(team.externalId, team.name));
    return map;
  }, [teams]);

  // Batch mode (DKPLAT-501 Phase 1): one group per selected fixture.
  if (fixtures) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading teams...</span>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive">
          <AlertDescription>Failed to load teams. Please try again.</AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>{title}</Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        {fixtures.map(fixture => (
          <div key={fixture.fixtureId} className="space-y-2">
            <Label className="text-sm font-semibold">{fixture.fixtureDisplay}</Label>
            <div className="space-y-2">
              {fixture.fixtureTeamIds.map(teamId => {
                const teamName = teamMap.get(teamId) || teamId;
                const isSelected = fixture.selectedTeamIds.includes(teamId);

                if (mode === 'locked') {
                  return (
                    <Card key={teamId} className="p-3 flex items-center justify-between">
                      <span className="font-medium">{teamName}</span>
                      <Badge variant="secondary">Included</Badge>
                    </Card>
                  );
                }

                const isOtherSelected = fixture.selectedTeamIds.length > 0 && !isSelected;

                return (
                  <Card
                    key={teamId}
                    data-testid={`team-${fixture.fixtureId}-${teamId}`}
                    className={`p-3 cursor-pointer ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                    onClick={() =>
                      !isOtherSelected &&
                      !isSelected &&
                      onSelectForFixture?.(fixture.fixtureId, teamId, teamName)
                    }
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isSelected}
                        disabled={isOtherSelected}
                        onCheckedChange={checked =>
                          checked
                            ? onSelectForFixture?.(fixture.fixtureId, teamId, teamName)
                            : onSelectForFixture?.(fixture.fixtureId, '', '')
                        }
                        // Stop the click from bubbling to the Card's onClick,
                        // which would re-select the team just unchecked.
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="font-medium flex-1">{teamName}</span>
                    </div>
                  </Card>
                );
              })}
            </div>
            {mode === 'radio' && fixture.selectedTeamIds.length === 0 && (
              <Alert>
                <AlertDescription>
                  Please select the favourite team for this fixture.
                </AlertDescription>
              </Alert>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (!leagueId || !fixtureTeamIds) {
    return (
      <Alert>
        <AlertDescription>Please select a fixture first.</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading teams...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load teams. Please try again.</AlertDescription>
      </Alert>
    );
  }

  if (fixtureTeams.length !== 2) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Expected 2 teams from fixture but found {fixtureTeams.length}. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const handleCheckboxChange = (teamId: string, teamName: string, checked: boolean) => {
    if (checked) {
      onSelect?.(teamId, teamName);
    } else {
      // Uncheck - clear selection
      onSelect?.('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{title}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-2">
        {fixtureTeams.map(team => {
          const isSelected = selectedTeamId === team.externalId;
          const isOtherTeamSelected = selectedTeamId && selectedTeamId !== team.externalId;

          return (
            <Card
              key={team.externalId}
              data-testid={`team-${team.externalId}`}
              className={`p-4 ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
            >
              <div className="flex items-center space-x-3">
                <Checkbox
                  id={`team-${team.externalId}`}
                  checked={isSelected}
                  disabled={!!isOtherTeamSelected} // Disable if another team is selected
                  onCheckedChange={checked =>
                    handleCheckboxChange(team.externalId, team.name, !!checked)
                  }
                />
                <div className="flex items-center gap-3 flex-1">
                  {team.logoUrl && (
                    <img src={team.logoUrl} alt={team.name} className="h-10 w-10 object-contain" />
                  )}
                  <label
                    htmlFor={`team-${team.externalId}`}
                    className="font-medium cursor-pointer flex-1"
                  >
                    {team.name}
                  </label>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {!selectedTeamId && (
        <Alert>
          <AlertDescription>Please select exactly one team to continue.</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
